import type { Translations } from '@/i18n/types';

const en: Translations = {
  /* ── Common ───────────────────────────────────────────────── */
  'common.add': 'Add',
  'common.save': 'Save',
  'common.create': 'Create',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.skip': 'Skip',
  'common.search': 'Search',
  'common.retry': 'Retry',
  'common.reloadPage': 'Reload Page',
  'offline.title': "You're Offline",
  'offline.message': 'You are currently not connected to the internet.',
  'common.loading': 'Loading…',
  'common.optional': '(optional)',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.none': '— None —',
  'common.notSet': '— Not set —',
  'common.deleting': 'Deleting…',
  'common.applying': 'Applying…',
  'common.closeDialog': 'Close dialog',
  'common.prev': 'Prev',
  'common.next': 'Next',

  /* ── Filter UI ────────────────────────────────────────────── */
  'filter.add': 'Add filter',
  'filter.allActive': 'All filters active',
  'filter.clearAll': 'Clear all',
  'filter.clear': 'Clear',
  'filter.apply': 'Apply',
  'filter.cancel': 'Cancel',
  'filter.condition': 'Condition',
  'filter.presets': 'Presets',
  'filter.customRange': 'Custom Range',
  'filter.from': 'From',
  'filter.to': 'To',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.value': 'Value',
  'filter.searchOptions': 'Search options...',
  'filter.noOptions': 'No options found',
  'filter.loadingOptions': 'Loading options...',

  /* ── Auth ─────────────────────────────────────────────────── */
  'auth.signIn': 'Sign In',
  'auth.signInWithGoogle': 'Sign in with Google',
  'auth.signInWithApple': 'Sign in with Apple',
  'auth.or': 'or',
  'auth.signOut': 'Sign Out',
  'auth.welcome': 'Welcome',
  'auth.subtitle': 'Kart Editor',
  'auth.signingIn': 'Signing in...',

  /* ── Settings ───────────────────────────────────────────────── */
  'settings.title': 'Settings',
  'settings.account': 'Account',
  'settings.profile': 'Profile',
  'settings.displayName': 'Display Name',
  'settings.saveName': 'Save Name',
  'settings.nameSaved': 'Name updated successfully',
  'settings.linkedAccounts': 'Linked Accounts',
  'settings.linkGoogle': 'Link Google Account',
  'settings.linkApple': 'Link Apple Account',
  'settings.unlink': 'Unlink',
  'settings.linked': 'Linked',
  'settings.notLinked': 'Not linked',
  'settings.cannotUnlinkOnlyProvider': 'At least one login method must remain linked.',
  'settings.language': 'Language',
  'settings.tracks': 'Tracks',
  'settings.categories': 'Categories',
  'settings.drivers': 'Drivers',
  'settings.userId': 'User ID',
  'settings.signOut': 'Sign Out',
  'settings.connectedApps': 'Connected Applications',
  'settings.connectedAppsDesc': 'Manage AI assistants, IDEs, and tools authorized to access your telemetry data via MCP.',
  'settings.noConnectedApps': 'No connected applications yet. Authorize an MCP client (such as Claude Desktop or Cursor) to see it here.',
  'settings.authorizedOn': 'Authorized',
  'settings.lastUsed': 'Last Used',
  'settings.neverUsed': 'Never',
  'settings.revoke': 'Revoke',
  'settings.revokeAppConfirm': 'Are you sure you want to revoke access for this application? Any active sessions will immediately stop working.',
  'settings.appRevokedSuccess': 'Application access revoked successfully',
  'settings.copyKey': 'Copy Snippet',
  'settings.keyCopied': 'Copied to clipboard',
  'settings.mcpConfigSnippet': 'MCP Client Configuration',
  'settings.mcpConfigDesc': 'Add this configuration to your Claude Desktop (claude_desktop_config.json) or Cursor MCP settings. Authentication will complete automatically via your browser:',
  'settings.deleteAccount': 'Delete Account',
  'settings.deleteAccountDesc': 'Permanently delete your account and all associated sessions, telemetry data, saved analyses, equipment, kart setups, and sharing entries.',
  'settings.deleteAccountConfirmTitle': 'Delete Account Permanently?',
  'settings.deleteAccountConfirmDesc': 'This action cannot be undone. All of your owned sessions, telemetry data, saved analyses, equipment, kart setups, teams, and shared session entries will be immediately and permanently deleted.',
  'settings.deleteAccountConfirmPrompt': 'To confirm, type "{phrase}" in the box below:',
  'settings.deleteAccountConfirmPhrase': 'delete my account',
  'settings.deleteAccountConfirmButton': 'Delete My Account',

  /* ── Common fields ────────────────────────────────────────── */
  'common.brand': 'Brand',
  'common.type': 'Type',
  'common.nr': 'Nr#',
  'common.nrInfo': 'Sequential number to identify this entry.',
  'common.year': 'Year',
  'common.stiffness': 'Stiffness',
  'common.width': 'Width',
  'common.position': 'Position',

  /* ── Tabs ──────────────────────────────────────────────────── */
  'tab.engines': 'Engines',
  'tab.tyres': 'Tyres',
  'tab.chassis': 'Chassis',
  'tab.carburetors': 'Carburetors',
  'tab.axles': 'Axles',
  'tab.hubs': 'Hubs',
  'tab.spark_plugs': 'Spark Plugs',
  'tab.tracks': 'Tracks',
  'tab.categories': 'Categories',
  'tab.drivers': 'Drivers',
  'tab.races': 'Races',
  'tab.users': 'Users',
  'tab.settings': 'Settings',

  /* ── Subtabs ──────────────────────────────────────────────── */

  /* ── Navigation ────────────────────────────────────────────── */
  'nav.administration': 'Administration',

  /* ── CRUD ──────────────────────────────────────────────────── */
  'crud.requiredField': '{field} is required',
  'crud.updated': '{entity} updated',
  'crud.created': '{entity} created',
  'crud.deleted': '{entity} deleted',
  'crud.confirmDelete': 'Delete {entity} "{name}"?\nThis cannot be undone.',
  'crud.emptyState': 'No {title} yet. Add your first {entity} above.',
  'crud.editEntity': 'Edit {entity}',
  'crud.newEntity': 'New {entity}',

  /* ── Entities ──────────────────────────────────────────────── */
  'entity.engine': 'Engine',
  'entity.tyre': 'Tyre',
  'entity.chassis': 'Chassis',
  'entity.carburetor': 'Carburetor',
  'entity.axle': 'Axle',
  'entity.hub': 'Hub',
  'entity.spark_plug': 'Spark Plug',
  'entity.track': 'Track',
  'entity.category': 'Category',
  'entity.driver': 'Driver',

  /* ── Sessions ──────────────────────────────────────────────── */

  /* ── Session Setup Dialog ──────────────────────────────────── */

  /* ── Search Help Dialog ────────────────────────────────────── */

  /* ── Import Dialog ─────────────────────────────────────────── */
  'import.title': 'Import Race PDFs',
  'import.uploadDesc': 'Upload RMC race result PDFs. They will be parsed and presented for review before importing.',
  'import.dragDropPdf': 'Drag & drop PDF files here',
  'import.orBrowse': 'or',
  'import.browseFiles': 'browse files',
  'import.onlyPdfAccepted': 'Only PDF files are accepted.',
  'import.filesSelected': '{n} file(s) selected',
  'import.upload': 'Upload & Parse',
  'import.uploadingPct': 'Uploading… {n}%',
  'import.parseFailed': 'Failed to parse the response.',
  'import.uploadFailed': 'Upload failed. Please try again.',
  'import.connectionLost': 'Connection lost.',
  'import.uploadAborted': 'Upload aborted.',
  'import.reviewDesc': 'Review parsed sessions. Deselect any you do not want to import.',
  'import.selectAll': 'Select all',
  'import.deselectAll': 'Deselect all',
  'import.existsInDb': 'Exists — will overwrite',
  'import.entriesCount': '{n} entries',
  'import.submit': 'Import',
  'import.submitting': 'Importing sessions…',
  'import.importedCount': '{n} session(s) imported.',
  'import.successMessage': 'Sessions imported successfully!',
  'import.noNewSessions': 'No new sessions were imported.',
  'import.imported': 'Imported',
  'import.skipped': 'Skipped',
  'import.errors': 'Errors',
  'import.importButton': 'Import PDFs',

  /* ── Kart Setup Dialog ─────────────────────────────────────── */

  /* ── Setup Tab ──────────────────────────────────────────────── */

  /* ── Tyre Pressures ────────────────────────────────────────── */

  /* ── Front Geometry ────────────────────────────────────────── */

  /* ── Rear Config ───────────────────────────────────────────── */

  /* ── Engine & Gear ──────────────────────────────────────────── */

  /* ── Carburetor Tuning ──────────────────────────────────────── */

  /* ── Chassis Select ─────────────────────────────────────────── */

  /* ── Notes ──────────────────────────────────────────────────── */

  /* ── Propagation Wizard ─────────────────────────────────────── */

  /* ── Propagation field labels ───────────────────────────────── */

  /* ── Tracks ─────────────────────────────────────────────────── */
  'tracks.name': 'Track Name',
  'tracks.latitude': 'Latitude',
  'tracks.longitude': 'Longitude',
  'tracks.searchAddress': 'Search for an address…',
  'tracks.coordinates': 'Coordinates',
  'tracks.clickToAdjust': 'Click the map to adjust the pin',
  'tracks.tabLocation': 'Location',
  'tracks.tabAliases': 'Aliases',
  'tracks.sessionTracks': 'Session Tracks',
  'tracks.raceEventTracks': 'Race Event Tracks',
  'tracks.noSessionTracks': 'No session tracks available',
  'tracks.noRaceEventTracks': 'No race event tracks available',
  'tracks.linkedAliases': '{sessions} session tracks · {races} race tracks',
  'tracks.satelliteImage': 'Satellite Image',
  'tracks.fetchImage': 'Fetch Satellite Image',
  'tracks.refetchImage': 'Refetch Satellite Image',
  'tracks.fetchingImage': 'Fetching image…',
  'tracks.imageFetched': 'Satellite image fetched successfully',
  'tracks.imageFetchFailed': 'Failed to fetch satellite image',
  'tracks.noImage': 'No satellite image',
  'tracks.imageZoom': 'Zoom Level',
  'tracks.autoZoom': 'Auto Zoom',

  /* ── Categories ─────────────────────────────────────────────── */
  'categories.name': 'Name',
  'categories.aliases': 'Category Aliases',
  'categories.noAliases': 'No category names available',
  'categories.linkedAliases': '{count} aliases linked',

  /* ── Races ──────────────────────────────────────────────────── */
  'races.title': 'Race Sessions',
  'races.loading': 'Loading race sessions…',
  'races.empty': 'No race sessions yet. Import race PDFs to get started.',
  'races.loadFailed': '⚠ Failed to load race sessions: {error}',
  'races.date': 'Date',
  'races.track': 'Track',
  'races.sessionType': 'Type',
  'races.category': 'Category',
  'races.entries': 'Entries',
  'races.laps': 'Laps',
  'races.championship': 'Championship',
  'races.deleteConfirmTitle': 'Delete Race Session',
  'races.deleteConfirmMessage': 'Are you sure you want to delete the <strong>{type}</strong> session at <strong>{track}</strong> on <strong>{date}</strong>? This will permanently remove all entries and lap data.',
  'races.deleteConfirmButton': 'Delete Session',
  'races.deleted': 'Race session deleted',
  'races.detailTitle': 'Race Session',
  'races.detailSubtitle': '{type} · {track} · {date}',
  'races.position': 'Pos',
  'races.kart': 'Kart',
  'races.driver': 'Driver',
  'races.firstName': 'First Name',
  'races.lastName': 'Last Name',
  'races.nationality': 'Nat',
  'races.grid': 'Grid',
  'races.bestLap': 'Best Lap',
  'races.totalTime': 'Total Time',
  'races.penalty': 'Pen',
  'races.gap': 'Gap',
  'races.status': 'Status',
  'races.deleteEntry': 'Delete entry',
  'races.entryDeleted': 'Entry deleted',
  'races.saved': 'Race session saved',
  'races.searchPlaceholder': 'Search races… e.g. Castelletto or type:race',
  'races.querySyntaxHelp': 'Query syntax help',
  'races.sessionCount': '{count} sessions',
  'races.sessionCountSingular': '1 session',

  /* ── Race Search Help Dialog ──────────────────────────────── */
  'raceSearchHelp.title': 'Race Search Syntax',
  'raceSearchHelp.subtitle': 'Filter race sessions using field:value pairs. Multiple terms are combined with AND.',
  'raceSearchHelp.bareText': '(bare text)',
  'raceSearchHelp.bareTextDesc': 'Matches track name or championship name containing the text.',
  'raceSearchHelp.track': 'track',
  'raceSearchHelp.trackDesc': 'Matches track name containing the value.',
  'raceSearchHelp.championship': 'championship',
  'raceSearchHelp.championshipDesc': 'Matches championship name containing the value.',
  'raceSearchHelp.category': 'category',
  'raceSearchHelp.categoryDesc': 'Matches category name containing the value.',
  'raceSearchHelp.type': 'type',
  'raceSearchHelp.typeDesc': 'Matches session type or label (e.g. race, qualifying, heat).',
  'raceSearchHelp.entries': 'entries',
  'raceSearchHelp.entriesDesc': 'Filter by entry count. Supports =, >, >=, <, <= and ranges.',
  'raceSearchHelp.laps': 'laps',
  'raceSearchHelp.lapsDesc': 'Filter by total lap count. Supports comparison operators and ranges.',
  'raceSearchHelp.season': 'season',
  'raceSearchHelp.seasonDesc': 'Filter by championship season year. Supports comparison operators.',
  'raceSearchHelp.from': 'from',
  'raceSearchHelp.fromDesc': 'Races on or after a date. Accepts YYYY-MM-DD or relative: -7d, -2w, -3m, -1y.',
  'raceSearchHelp.to': 'to',
  'raceSearchHelp.toDesc': 'Races on or before a date. Same formats as "from".',
  'raceSearchHelp.operatorsTitle': 'Operators & Logic',
  'raceSearchHelp.numericComparisons': 'Numeric comparisons:',
  'raceSearchHelp.range': 'Range:',
  'raceSearchHelp.rangeDesc': '— matches values between 5 and 20 inclusive.',
  'raceSearchHelp.booleanLogic': 'Boolean logic:',
  'raceSearchHelp.booleanLogicDesc': 'Terms are implicitly joined with',
  'raceSearchHelp.quotes': 'Quotes:',
  'raceSearchHelp.quotesDesc': 'Wrap values with spaces in double quotes, e.g.',
  'raceSearchHelp.examplesTitle': 'Example Queries',
  'raceSearchHelp.example1Desc': 'Race sessions at Castelletto',
  'raceSearchHelp.example2Desc': 'RMC championship races from the last year',
  'raceSearchHelp.example3Desc': 'Races with 20+ entries and 5+ laps',

  /* ── Pagination ─────────────────────────────────────────────── */
  'pagination.eventsFound': '{n} event(s) found',

  /* ── Tyre IDs ──────────────────────────────────────────────── */

  /* ── Settings ──────────────────────────────────────────────── */
  'settings.users': 'Users',

  /* ── Drivers ──────────────────────────────────────────────── */
  'drivers.canonicalDriver': 'Canonical Driver',
  'drivers.aliases': 'Aliases',
  'drivers.aliasesHeader': 'Aliases',
  'drivers.raceDrivers': 'Race Drivers',
  'drivers.sessionDrivers': 'Session Drivers',
  'drivers.noRaceDrivers': 'No race drivers available',
  'drivers.noSessionDrivers': 'No session drivers available',
  'drivers.selectDriver': 'Select a driver…',
  'drivers.searchDrivers': 'Search drivers…',
  'drivers.createTitle': 'New Driver Alias',
  'drivers.editTitle': 'Edit Driver Alias',
  'drivers.stepCanonical': 'Select Driver',
  'drivers.stepAliases': 'Select Aliases',

  /* ── Sharing ─────────────────────────────────────────────── */
  
  /* ── Users ──────────────────────────────────────────────── */
  'users.title': 'Users',
  'users.empty': 'No users found',
  'users.loading': 'Loading users...',
  'users.searchPlaceholder': 'Search users...',
  'users.name': 'Name',
  'users.email': 'Email',
  'users.user': 'User',
  'users.admin': 'Admin',
  'users.owner': 'Owner',
  'users.promote': 'Promote',
  'users.demote': 'Demote',
  'users.promoted': '{name} promoted to admin',
  'users.demoted': '{name} removed as admin',
  'users.cannotDemoteOwner': 'The owner cannot be demoted',
  'users.cannotDemoteSelf': 'You cannot demote yourself',
  'users.confirmPromote': 'Promote {name} to administrator? They will gain full access to the editor.',
  'users.confirmDemote': 'Remove administrator access from {name}? They will no longer be able to use the editor.',
  'users.accessDenied': 'Access Denied',
  'users.accessDeniedMessage': 'You do not have administrator privileges. Contact an administrator to request access.',
  'users.joinedAt': 'Joined',
  'users.lastLogin': 'Last login',
  'users.count': '{count} users',
  'users.countSingular': '1 user',

  /* ── User Search Help Dialog ────────────────────────────── */
  'userSearchHelp.title': 'User Search',
  'userSearchHelp.subtitle': 'Filter the user list using field-specific or freeform queries.',
  'userSearchHelp.bareText': 'Free text',
  'userSearchHelp.bareTextDesc': 'Matches against both name and email address.',
  'userSearchHelp.name': 'name:',
  'userSearchHelp.nameDesc': 'Filter by user display name.',
  'userSearchHelp.email': 'email:',
  'userSearchHelp.emailDesc': 'Filter by email address.',
  'userSearchHelp.login': 'login:',
  'userSearchHelp.loginDesc': 'Filter by last login date. Supports single values (-7d) or ranges (-30d..today). Use relative dates (-7d, -2w, -3m), absolute (2026-01-15), or today.',
  'userSearchHelp.created': 'created:',
  'userSearchHelp.createdDesc': 'Filter by account creation date. Supports single values (-30d) or ranges (-1y..today). Use relative dates (-7d, -2w, -3m), absolute (2026-01-15), or today.',
  'userSearchHelp.examplesTitle': 'Examples',
  'userSearchHelp.example1Desc': 'Find users named Yanick',
  'userSearchHelp.example2Desc': 'Find users with Gmail addresses',
  'userSearchHelp.example3Desc': 'Find users who logged in within the last 7 days',
  'userSearchHelp.example4Desc': 'Find users created in a date range',

  /* ── Bugs tab ───────────────────────────────────────────────── */
  'tab.bugs': 'Bugs',
  'bugs.title': 'Bug Reports',
  'bugs.empty': 'No bug reports found.',
  'bugs.loading': 'Loading bug reports…',
  'bugs.searchPlaceholder': 'Search bug reports…',
  'bugs.reporter': 'Reporter',
  'bugs.description': 'Description',
  'bugs.pageUrl': 'Page URL',
  'bugs.status': 'Status',
  'bugs.statusOpen': 'Open',
  'bugs.statusResolved': 'Resolved',
  'bugs.createdAt': 'Created',
  'bugs.resolvedAt': 'Resolved at',
  'bugs.resolve': 'Resolve',
  'bugs.reopen': 'Reopen',
  'bugs.resolved': 'Bug report resolved.',
  'bugs.reopened': 'Bug report reopened.',
  'bugs.count': '{count} reports',
  'bugs.countSingular': '1 report',
  'bugs.detailTitle': 'Bug Report',
  'bugs.userAgent': 'User Agent',
  'bugs.screenSize': 'Screen Size',
  'bugs.appLanguage': 'Language',
  'bugs.copy': 'Copy text',
  'bugs.copied': 'Copied to clipboard',

  /* ── Bug search help ────────────────────────────────────────── */
  'bugSearchHelp.title': 'Search Syntax',
  'bugSearchHelp.subtitle': 'Use these prefixes to narrow your search.',
  'bugSearchHelp.bareText': 'free text',
  'bugSearchHelp.bareTextDesc': 'Searches in description and reporter name.',
  'bugSearchHelp.status': 'status:open',
  'bugSearchHelp.statusDesc': 'Filter by status (open or resolved).',
  'bugSearchHelp.email': 'email:value',
  'bugSearchHelp.emailDesc': 'Filter by reporter email.',
  'bugSearchHelp.examplesTitle': 'Examples',
  'bugSearchHelp.example1Desc': 'Find all open bug reports.',
  'bugSearchHelp.example2Desc': 'Find bug reports from Gmail users.',

  /* ── Error auto-reporting & Error Boundary ─────────────────────────── */
  'error.autoReported': 'An error occurred and has been automatically reported.',
  'errorBoundary.title': 'Something went wrong',
  'errorBoundary.message': 'An unexpected error occurred and has been reported to the server.',
  'errorBoundary.reload': 'Reload Page',
  'errorBoundary.reset': 'Try Again',
  'errorBoundary.showDetails': 'Show error details',
  'errorBoundary.hideDetails': 'Hide error details',

  /* ── Profiles ───────────────────────────────────────────────── */
  'tab.profiles': 'Profiles',
  'profiles.engineProfiles': 'Engine Profiles',
  'profiles.tyreProfiles': 'Tyre Profiles',
  'profiles.addEngineProfile': 'Add Engine Profile',
  'profiles.editEngineProfile': 'Edit Engine Profile',
  'profiles.addTyreProfile': 'Add Tyre Profile',
  'profiles.editTyreProfile': 'Edit Tyre Profile',
  'profiles.brand': 'Brand',
  'profiles.type': 'Type',
  'profiles.compound': 'Compound',
  'profiles.compoundDry': 'Dry',
  'profiles.compoundWet': 'Wet',
  'profiles.deleteBlocked': 'Cannot delete — still referenced by equipment.',
  'profiles.emptyEngineProfiles': 'No engine profiles yet.',
  'profiles.emptyTyreProfiles': 'No tyre profiles yet.',
  'entity.engineProfile': 'Engine Profile',
  'entity.tyreProfile': 'Tyre Profile',

  /* ── Track Shape Studio ────────────────────────────────────── */
  'trackStudio.title': 'Track Shape & Annotation Studio',
  'trackStudio.subtitle': 'Design circuit geometry, edit splines, and place turn/sector annotations',
  'trackStudio.openStudio': 'Track Geometry & Annotations',
  'trackStudio.modeSpline': 'Geometry & Splines',
  'trackStudio.modeCorners': 'Corners',
  'trackStudio.modeSectors': 'Sectors',
  'trackStudio.modeSegments': 'Segments',
  'trackStudio.bakeSegments': 'Bake 200 Segments',
  'trackStudio.rebakeSegments': 'Re-bake 200 Segments',
  'trackStudio.segmentsBakedSuccess': 'Baked {count} track segments successfully',
  'trackStudio.segmentsCount': '{count} Segments',
  'trackStudio.segmentsStatusBaked': '200 Segments Baked',
  'trackStudio.segmentsStatusNotBaked': 'Not baked yet',
  'trackStudio.segmentsHint': 'Bakes the track middle line into 200 equidistant static GPS segments starting from the Start / Finish line for high-precision lap comparison.',
  'trackStudio.segmentDetails': 'Segment Details',
  'trackStudio.segmentIndex': 'Segment #{index}',
  'trackStudio.avgSegmentLength': 'Avg. Segment Length',
  'trackStudio.searchSegments': 'Search segments...',
  'trackStudio.modeAnnotations': 'Corner & Sector Annotations',
  'trackStudio.cornersCount': 'Track Corners ({count})',
  'trackStudio.sectorsCount': 'Timing Sectors ({count})',
  'trackStudio.clickCornersHint': 'Click anywhere on the track ribbon to add a Turn. Drag apex to move or adjust entry and exit points.',
  'trackStudio.clickSectorsHint': 'Click anywhere on the track ribbon to place a Sector split marker. Drag markers to reposition along the circuit.',
  'trackStudio.trackLength': 'Track Length: {length}m',
  'trackStudio.trackWidth': 'Track Width',
  'trackStudio.defaultTrackWidth': 'Default Track Width',
  'trackStudio.pointWidth': 'Point Width',
  'trackStudio.customWidth': 'Custom width for this point',
  'trackStudio.useDefaultWidth': 'Use default ({width}m)',
  'trackStudio.resetPointWidth': 'Reset to Default',
  'trackStudio.segmentWidth': 'Segment Width Tool',
  'trackStudio.segmentWidthDesc': 'Adjust width or create smooth width transitions between points',
  'trackStudio.fromPoint': 'From Point',
  'trackStudio.toPoint': 'To Point',
  'trackStudio.startWidth': 'Start Width',
  'trackStudio.endWidth': 'End Width',
  'trackStudio.transitionMode': 'Transition Mode',
  'trackStudio.modeConstant': 'Constant Width',
  'trackStudio.modeGradient': 'Smooth Transition (Gradient)',
  'trackStudio.applySegment': 'Apply Width to Segment',
  'trackStudio.resetSegment': 'Reset Segment Width',
  'trackStudio.segmentApplied': 'Updated width for points {from} to {to}',
  'trackStudio.controlPoints': 'Centerline Control Points ({count})',
  'trackStudio.addPointHint': 'Click on the curve or canvas to insert control points. Drag points to reshape.',
  'trackStudio.selectedPoint': 'Selected Point #{index}',
  'trackStudio.deletePoint': 'Delete Point',
  'trackStudio.evenSpacePoints': 'Evenly Space Points',
  'trackStudio.reverseDirection': 'Reverse Track Direction',
  'trackStudio.layerSatellite': 'Satellite Image',
  'trackStudio.layerCenterline': 'Centerline Spline',
  'trackStudio.layerBoundaries': 'Track Boundaries',
  'trackStudio.layerAnnotations': 'Annotations & Markers',
  'trackStudio.undo': 'Undo (Cmd+Z)',
  'trackStudio.redo': 'Redo (Cmd+Shift+Z)',
  'trackStudio.reset': 'Reset Changes',
  'trackStudio.saveSuccess': 'Track shape and annotations saved successfully!',
  'trackStudio.autoSaveSuccess': 'Track shape and annotations auto-saved',
  'trackStudio.saveError': 'Failed to save track shape: {error}',
  'trackStudio.noCenterline': 'Click on the satellite image to create the first control point for the track centerline.',
  'trackStudio.annotations': 'Track Annotations ({count})',
  'trackStudio.addCorner': 'Add Corner (T{next})',
  'trackStudio.addSector': 'Add Sector Marker',
  'trackStudio.addFinishLine': 'Set Start / Finish Line',
  'trackStudio.cornerName': 'Corner Name',
  'trackStudio.cornerDirection': 'Turn Direction',
  'trackStudio.directionLeft': 'Left Turn',
  'trackStudio.directionRight': 'Right Turn',
  'trackStudio.entry': 'Entry (Turn-in / Braking)',
  'trackStudio.apex': 'Apex (Clipping Point)',
  'trackStudio.exit': 'Exit (Track-out / Full Throttle)',
  'trackStudio.deleteAnnotation': 'Delete Annotation',
  'trackStudio.noAnnotations': 'No annotations created yet. Click on the track shape to place turns, sectors, or the start/finish line.',
  'trackStudio.finishLinePlaced': 'Finish Line is active at {pos}% of lap.',
  'trackStudio.closeConfirm': 'You have unsaved changes. Do you really want to close the Studio?',
  'trackStudio.menuAddCorner': 'Add Corner / Turn',
  'trackStudio.menuAddSector': 'Add Sector Marker',
  'trackStudio.menuSetFinishLine': 'Set Start / Finish Line',
  'trackStudio.menuTrackPosition': 'Track Position',
  'trackStudio.clickTrackHint': 'Click anywhere on the track ribbon to add a Turn, Sector marker, or set the Start/Finish line.',
  'trackStudio.drivingDirection': 'Driving Direction',
  'trackStudio.reverseDrivingDirection': 'Reverse Driving Direction',
  'trackStudio.drivingDirectionDesc': 'Flip the circuit driving direction. All corner sequences and timing sectors will automatically adapt.',
  'trackStudio.multiplePointsSelected': '{count} Points Selected',
  'trackStudio.selectAll': 'Select All (Cmd+A)',
  'trackStudio.deselectAll': 'Deselect (Esc)',
  'trackStudio.deleteSelectedPoints': 'Delete {count} Points',
  'trackStudio.customWidthSelection': 'Custom Width Override (All Selected)',
  'tracks.reanalyzeLaps': 'Re-analyze Track Laps',
  'tracks.reanalyzeAllLaps': 'Re-analyze All Laps',
  'tracks.reanalyzeQueued': 'Queued {enqueued} lap(s) for analysis ({skipped} already up-to-date).',
  'tracks.reanalyzeForceQueued': 'Queued {enqueued} lap(s) for full recalculation.',
  'tracks.reanalyzeAllUpToDate': 'All {skipped} lap(s) are already up-to-date with current track data.',
  'tracks.reanalyzeConfirmTitle': 'Re-analyze Track Laps',
  'tracks.reanalyzeConfirmMessage': 'The track data hash for "{name}" has not changed ({count} lap(s) are already up-to-date). Do you want to force a full recalculation for this track anyway?',
  'tracks.reanalyzeConfirmButton': 'Re-analyze Laps',
  'tracks.reanalyzeAllConfirmTitle': 'Re-analyze All Laps',
  'tracks.reanalyzeAllConfirmMessage': 'Are you sure you want to trigger a full recalculation of all laps and sessions across all tracks?',

  /* ── Monitoring ────────────────────────────────────────────── */
  'tab.monitoring': 'Monitoring',
  'monitoring.title': 'Queue Monitoring',
  'monitoring.subtitle': 'Real-time lap & session analysis queue statistics and processing controls',
  'monitoring.refresh': 'Refresh',
  'monitoring.autoRefreshActive': 'Auto-refreshing every 3s while active',
  'monitoring.lapQueueTitle': 'Lap Analysis Queue',
  'monitoring.sessionQueueTitle': 'Session Analysis Queue',
  'monitoring.pending': 'Pending',
  'monitoring.processing': 'Processing',
  'monitoring.completed': 'Completed',
  'monitoring.failed': 'Failed',
  'monitoring.totalEligible': 'Total Eligible',
  'monitoring.actionsTitle': 'Global Queue Controls',
  'monitoring.syncUnanalyzed': 'Sync Unanalyzed & Outdated',
  'monitoring.syncUnanalyzedDesc': 'Scan all tracks and enqueue any unanalyzed laps or laps with outdated track geometry',
  'monitoring.syncUnanalyzedDone': 'Queued {enqueued} lap(s) ({skipped} already up-to-date).',
  'monitoring.retryFailed': 'Retry Failed Jobs',
  'monitoring.retryFailedDesc': 'Reset all failed lap and session analysis jobs back to pending',
  'monitoring.retryFailedDone': 'Reset {retried} failed job(s) to pending.',
  'monitoring.reanalyzeAll': 'Force Re-analyze All Laps',
  'monitoring.reanalyzeAllDesc': 'Force a full recalculation of all laps and sessions across all tracks',
  'monitoring.userQueuesTitle': 'Per-User Queue Progress (Round-Robin)',
  'monitoring.userQueuesSubtitle': 'Worker claims jobs in round-robin order across users so large batches never starve other users',
  'monitoring.userQueuesEmpty': 'No active or queued jobs per user.',
  'monitoring.user': 'User',
  'monitoring.systemQueue': 'System / Global',
  'monitoring.lapsStatus': 'Lap Jobs',
  'monitoring.sessionsStatus': 'Session Jobs',
  'monitoring.progress': 'Progress',
  'monitoring.tracksTitle': 'Track Analysis Coverage & Re-trigger Controls',
  'monitoring.tracksSubtitle': 'Inspect lap analysis coverage per canonical track and trigger incremental or forced recalculation',
  'monitoring.tracksEmpty': 'No canonical tracks configured yet.',
  'monitoring.trackCoverage': 'Coverage',
  'monitoring.upToDateLaps': 'Up-to-date',
  'monitoring.outdatedLaps': 'Outdated',
  'monitoring.unanalyzedLaps': 'Unanalyzed',
  'monitoring.shapeReady': '200 Segments Ready',
  'monitoring.noShape': 'No Baked Segments',
  'monitoring.reanalyzeTrack': 'Sync / Re-analyze',
  'monitoring.forceReanalyzeTrack': 'Force All',

  /* ── Terms & Conditions ────────────────────────────────────── */
  'terms.gateTitle': 'Terms & Conditions',
  'terms.gateSubtitle': 'Please review and accept the latest Terms & Conditions to continue.',
  'terms.updatedBannerTitle': 'Updated Terms & Conditions:',
  'terms.updatedBannerDesc': 'Our Terms & Conditions have been updated. Please review and accept the new version to continue.',
  'terms.checkboxLabel': 'I have read and agree to the Terms & Conditions',
  'terms.acceptButton': 'Accept & Continue',
  'terms.acceptingButton': 'Accepting…',
  'terms.loadError': 'Unable to load the Terms & Conditions. Please check your connection and try again.',
  'terms.acceptError': 'Failed to record your acceptance. Please try again.',

  /* ── Search & Filter Bar ───────────────────────────────────── */
  'search.placeholder': 'Search or add filter...',
  'search.button': 'Search',
  'search.syntaxHelp': 'Search Syntax Help',
  'search.title': 'Search Syntax Guide',
  'search.subtitle': 'Filter and search using structured fields and boolean operators.',
  'search.closeDialog': 'Close',
  'search.field.bareTextDesc': 'Search across default text fields',
  'search.operatorsTitle': 'Operators',
  'search.numericComparisons': 'Numeric / Date Comparisons',
  'search.range': 'Range (inclusive)',
  'search.rangeDesc': 'Match values between min and max',
  'search.booleanLogic': 'Boolean Logic',
  'search.booleanLogicDesc': 'Combine multiple conditions',
  'search.booleanLogicUse': 'Combine terms with',
  'search.booleanLogicOr': 'or',
  'search.booleanLogicGroup': 'and group expressions with parentheses',
  'search.quotes': 'Quoted Strings',
  'search.quotesDesc': 'Wrap multi-word values in quotes (e.g. track:"South Garda")',
  'search.examplesTitle': 'Examples',

  'filter.operator.eq': 'is',
  'filter.operator.neq': 'is not',
  'filter.operator.gt': 'greater than',
  'filter.operator.gte': 'at least',
  'filter.operator.lt': 'less than',
  'filter.operator.lte': 'at most',
  'filter.operator.range': 'between',
  'filter.operator.contains': 'contains',
  'filter.remove': 'Remove filter',
  'filter.done': 'Done',
  'filter.clearInput': 'Clear input',
  'filter.selectField': 'Filter by field',
  'filter.pressEnter': 'Press Enter to apply',
  'filter.selectValues': 'Select values',
  'filter.noMatchingOptions': 'No matching options',

  'filter.bugs.bareSearchExample': 'Search summary, description, or reporter',
  'filter.bugs.searchSummary': 'Filter by bug summary',
  'filter.bugs.searchDescription': 'Filter by bug description',
  'filter.bugs.searchReporter': 'Filter by reporter name or email',
  'filter.bugs.filterStatus': 'Filter by bug status',
  'filter.bugs.filterSeverity': 'Filter by severity level',
  'filter.bugs.filterPlatform': 'Filter by platform (web, ios, android)',
  'filter.bugs.filterDate': 'Filter by report date',
  'filter.bugs.exampleOpenHigh': 'Open high-severity bugs',
  'filter.bugs.exampleMobileLogin': 'Mobile login issues',

  'filter.users.bareSearchExample': 'Search name, email, or user ID',
  'filter.users.searchName': 'Filter by display name',
  'filter.users.searchEmail': 'Filter by email address',
  'filter.users.searchId': 'Filter by user ID',
  'filter.users.filterRole': 'Filter by assigned role',
  'filter.users.exampleAdmin': 'Users with the admin role',
  'filter.users.exampleMultiRole': 'Admins or editors matching name',

  'filter.races.bareSearchExample': 'Search season, track, event, or driver',
  'filter.races.searchSeason': 'Filter by championship season name',
  'filter.races.filterYear': 'Filter by championship year',
  'filter.races.searchCountry': 'Filter by country code',
  'filter.races.searchCategory': 'Filter by racing category',
  'filter.races.searchTrack': 'Filter by track name',
  'filter.races.searchEvent': 'Filter by event title',
  'filter.races.filterRound': 'Filter by round number',
  'filter.races.searchSession': 'Filter by session name',
  'filter.races.filterType': 'Filter by session type',
  'filter.races.searchDriver': 'Filter by driver name',
  'filter.races.typePractice': 'Practice',
  'filter.races.typeQualifying': 'Qualifying',
  'filter.races.typeHeat': 'Elimination Heat',
  'filter.races.typePrefinal': 'Pre-Final',
  'filter.races.typeFinal': 'Final',
  'filter.races.exampleRokTrack': 'ROK Cup events at South Garda',
  'filter.races.exampleFinalDriver': 'Final sessions featuring a driver',

  'races.season': 'Season',
  'races.year': 'Year',
  'races.country': 'Country',
  'races.event': 'Event',
  'races.roundLabel': 'Round',
  'races.session': 'Session',
  'races.type': 'Type',
  'races.label': 'Label',
  'races.time': 'Time',

  'trackStudio.zoomIn': 'Zoom In',
  'trackStudio.zoomOut': 'Zoom Out',
  'trackStudio.resetView': 'Reset View',
  'trackStudio.geoLocation': 'Geo Location',
  'trackStudio.finishLineTitle': 'Start / Finish Line',
  'trackStudio.finishLineHint': 'Drag the flag handle along the track on the canvas, click on the track, or use the slider below.',
  'trackStudio.staticGpsPosition': 'Static GPS Position',
  'trackStudio.distance': 'Distance',
  'trackStudio.previous': 'Previous',
  'trackStudio.next': 'Next',

  'users.terms': 'Terms',
  'users.termsAccepted': 'Accepted',
  'users.termsOutdated': 'Outdated',
  'users.termsPending': 'Pending',
  'users.resetTerms': 'Reset Terms & Conditions',
  'users.confirmResetTerms': 'Reset Terms & Conditions acceptance for {name}? They will be required to review and accept the Terms & Conditions again before continuing.',
  'users.termsReset': 'Reset Terms & Conditions acceptance for {name}',
  // Filter Schema & UI Translations
  'filter.filterBy': 'Filter by {label}',
  'filter.removeFilter': 'Remove {label} filter',
  'filter.prevMonth': 'Previous month',
  'filter.nextMonth': 'Next month',
  'filter.selectStartDate': 'Select start date in calendar',
  'filter.op.exact': 'Exact',
  'filter.op.atLeast': 'At least',
  'filter.op.atMost': 'At most',
  'filter.op.between': 'Between',
  'filter.op.greater': 'Greater',
  'filter.op.less': 'Less',
  'filter.preset.today': 'Today',
  'filter.preset.last7d': 'Last 7 days',
  'filter.preset.last30d': 'Last 30 days',
  'filter.preset.last90d': 'Last 90 days',
  'filter.preset.last1y': 'Last 1 year',
  'filter.preset.last7dShort': 'Last 7d',
  'filter.preset.last30dShort': 'Last 30d',
  'filter.preset.last90dShort': 'Last 90d',
  'filter.preset.last1yShort': 'Last 1y',
  'filter.sessionType.race': 'Race',
  'filter.sessionType.final': 'Final',
  'filter.sessionType.pre_final': 'Pre-Final',
  'filter.sessionType.heat': 'Heat',
  'filter.sessionType.qualifying': 'Qualifying',
  'filter.unit.laps': 'laps',
  'filter.field.date': 'Date',
  'filter.field.track': 'Track',
  'filter.desc.track': 'Matches track name',
  'filter.field.championship': 'Championship',
  'filter.desc.championship': 'Championship series name',
  'filter.field.category': 'Category',
  'filter.desc.category': 'Canonical racing category',
  'filter.field.sessionType': 'Session Type',
  'filter.desc.sessionType': 'Session classification (race, heat, qualifying, final)',
  'filter.field.laps': 'Laps',
  'filter.field.season': 'Season',
  'filter.desc.season': 'Matches season year',
  'filter.field.driver': 'Driver',
  'filter.placeholder.driver': 'e.g. Yanick',
  'filter.placeholder.email': 'e.g. gmail.com',
  'filter.desc.email': 'Matches email address',
  'bugs.severity': 'Severity',
  'bugs.severityCritical': 'Critical',
  'bugs.severityHigh': 'High',
  'bugs.severityMedium': 'Medium',
  'bugs.severityLow': 'Low',
  'bugs.statusInProgress': 'In Progress',
  'bugs.statusClosed': 'Closed',
  'bugs.submitter': 'Submitter Email',
  'filter.bugs.title': 'Bug Report Search Syntax',
  'filter.bugs.subtitle': 'Filter reported bugs and issues by status, severity, submitter email, or keywords.',
  'filter.field.bugTitle': 'Summary / Title',
  'filter.placeholder.bugTitle': 'e.g. crash, upload',
  'filter.desc.bugTitle': 'Matches issue title or description',
  'filter.desc.bugStatus': 'Issue lifecycle status (open, in_progress, closed)',
  'filter.desc.bugSeverity': 'Bug severity level',
  'filter.placeholder.bugEmail': 'e.g. user@example.com',
  'filter.desc.bugEmail': 'Matches submitter email',
  'filter.field.bugEnv': 'Environment',
  'filter.desc.bugEnv': 'Filter by reporting environment (prod, dev, local)',
  'filter.bugs.example1': 'All open bug reports',
  'filter.bugs.example2': 'Open issues in production',
  'filter.bugs.example3': 'Open crash reports',
  'filter.field.driverEntries': 'Driver Entries',
  'filter.desc.driverEntries': 'Total number of participating drivers in the race',
  'filter.preset.entriesSmall': 'Small (< 10)',
  'filter.preset.entriesMedium': 'Medium (10..25)',
  'filter.preset.entriesLarge': 'Large (> 25)',
  'filter.desc.raceLaps': 'Number of completed race laps',
  'filter.desc.raceDriver': 'Matches driver name in race results',
  'filter.desc.raceDate': 'Filter races by date preset or specific calendar date range',
  'filter.races.example1': '2026 races at Wohlen',
  'filter.races.example2': 'Large Senior MAX grids',
  'filter.races.example3': 'Races with Yanick in the last year',
  'filter.users.title': 'User Search Syntax',
  'filter.users.subtitle': 'Filter registered platform users by name, email, permission role, or auth provider.',
  'users.colName': 'Name',
  'users.colEmail': 'Email',
  'users.colRole': 'Role',
  'users.colProvider': 'Auth Provider',
  'users.roleAdmin': 'Admin',
  'users.roleOwner': 'Owner',
  'users.roleUser': 'User',
  'filter.desc.userName': 'Matches user display name',
  'filter.desc.userRole': 'User permission role (admin, owner, user)',
  'filter.desc.authProvider': 'Authentication provider',
  'filter.provider.password': 'Password',
  'filter.desc.userDate': 'Created or last active date preset or range',
  'filter.users.example1': 'Find users matching Yanick',
  'filter.users.example2': 'Platform administrators',
  'filter.users.example3': 'Google logins in the last 30 days',
};

export default en;

